import React, { useState, useEffect, useRef } from "react";
import {
  differenceInDays,
  differenceInHours,
  differenceInMinutes,
} from "date-fns";
import moment from "moment";

import { DataServiceContext } from "./context";
import {
  dbKeyAllFlights,
  dbKeySelectedFlight,
  getUnixTimeInSeconds,
  dbKeyLastCacheTimestamp,
  dbKeyCacheResetTimestamp,
  get,
  set,
  dbKeyAirportSelected,
  dbKeySNOWTAM,
  dbKeyMETAR,
  dbKeyTAF,
  dbKeyNOTAM,
  dbKeyNOTAMByICAO,
  dbKeyCTOT,
  dbKeyCTOTCrew,
  dbKeyCTOTAck,
  dbKeyCIF,
  dbKeyFlightInfoCrew,
  dbKeyFlightData,
  dbKeyTimestamp,
  clear,
  dbKeyReqFuel,
  dbKeyAirports,
  dbKeyAreaMessages,
  dbKeyPreFlightBriefing,
  dbKeyTkofSettings,
  dbKeyWXGImages,
  dbKeyDMSFiles,
  dbKeyOFPFiles,
  dbKeyPassengerList,
  flattenDmsList,
  isCabinCrew,
  isTrainingMode,
  splitFlightCode,
  dbKeySeatmap,
  dbKeyMealMode,
  dbKeyColdSoak,
  dbKeyBagList,
  pollCTOTDataHelper,
  getFileType,
  resolveOfpFuelReset,
  resolvePreFlightBriefing,
  sanitizeBriefingHtml,
  inlineBriefingImages,
  readUnseenBriefingId,
  readKnownBriefingId,
  writeUnseenBriefingId,
  writeKnownBriefingId,
  shouldFlagBriefingNew,
  consumeEndedBriefingSession,
} from "../../helpers";
import {
  getSNOWTAM,
  getMETAR,
  getTAFV2,
  fetchNOTAM,
  getFlts,
  getFlt,
  getCTOT,
  getCTOTCrew,
  getCIF,
  getFltInfoCrew,
  getAreaMessages as getAreaMsgs,
  getPreFlightBriefing,
  fetchUserAuthorization,
  fetchPassengerList,
  fetchSeatmap,
  fetchColdSoak,
  fetchBagList,
  isFlightPlanAirport,
  stripFaaNotamFromResponse,
} from "../../utilities/helperfunctions";
import { fetchTKOFSettings } from "../../utilities/perfCalcHelperFunctions";
import {
  SESSION_EXPIRED,
  TRAINING_MODE_EMPLOYEE_NUMBER,
} from "../../utilities/enums";
import {
  findFlight,
  filterFlightsInRetentionWindow,
  hasFlightsNext14h,
  hasFlightsLast72h,
  getFirstActiveFlightId,
  getNextUpcomingFlightId,
  getNextUpcomingFlights,
  getMostRecentPastFlightId,
} from "./helpers";
import { defaultFlightData } from "./context";
import { clearIfExceedsLimit, remove } from "../../helpers/indexedDB";

const briefingState = (topic) => ({
  preFlightBriefing: topic,
  ...(Number.isInteger(topic?.id) && topic.id === readUnseenBriefingId()
    ? { preFlightBriefingIsNew: true }
    : {}),
});

// Library for parsing XML
const parseString = require("xml2js").parseString;

export const DataServiceProvider = ({ children, employeeNumber }) => {
  const [flightData, setFlightData] = useState(defaultFlightData);
  const flightDataRef = useRef();
  const prefetchedFlightIdsRef = useRef(new Set());
  const fetchingNOTAMRef = useRef(new Set());
  const pendingSelectedFlightIdRef = useRef(null);
  const loadingFlightIdsRef = useRef(new Set());
  const flightListSyncingRef = useRef(false);
  const [flightListSyncId, setFlightListSyncId] = useState(0);

  // ref ensures access to updated data instantly
  flightDataRef.current = flightData;

  const isFlightActive = (flightId) => {
    if (!flightId) return false;
    const currentSelected = flightDataRef.current?.selectedFlightId;
    const pendingSelected = pendingSelectedFlightIdRef.current;
    return flightId === currentSelected || flightId === pendingSelected;
  };

  const resolveStoredReqFuel = (value) =>
    value == null || value === "" ? defaultFlightData.reqFuel : value;

  // Check if training mode is active and override employeeNumber to training mode employee number
  const getEffectiveEmployeeNumber = () => {
    const trainingMode = isTrainingMode();
    return trainingMode ? TRAINING_MODE_EMPLOYEE_NUMBER : employeeNumber;
  };

  const handleCacheReset = async () => {
    const lastCacheResest = await getLocalData(dbKeyCacheResetTimestamp);
    const now = getUnixTimeInSeconds();

    if (!lastCacheResest) {
      saveLocalData(now, dbKeyCacheResetTimestamp);
      return;
    }

    // Clear cache every 30 days to avoid large and unused data storage
    if (differenceInDays(now, lastCacheResest) >= 30) {
      clear();
      saveLocalData(now, dbKeyCacheResetTimestamp);
    }
  };

  useEffect(() => {
    const effectiveEmployeeNumber = getEffectiveEmployeeNumber();
    if (effectiveEmployeeNumber || !navigator.onLine) {
      init();
    }
  }, [employeeNumber]);

  const init = async () => {
    // Clear any previous error status when initializing
    updateFlightData({ status: "" });

    if (navigator.onLine) {
      // Clear cache storage usage > 50 MB
      await clearIfExceedsLimit();
      await handleCacheReset();

      const effectiveEmployeeNumber = getEffectiveEmployeeNumber();
      if (!effectiveEmployeeNumber) {
        return;
      }

      await fetchUserAuthorization(effectiveEmployeeNumber)
        .then((res) => res.json())

        // Used in production to check if user is authorized
        .then(async (response) => {
          if (response?.[0]?.authBool === false) {
            updateFlightData({ status: "Not authorized" });
          } else {
            updateFlightData({ status: "" });
            await getAllFlights();
            startPolling();
          }
        })
        .catch((error) => console.log(error));

      // Used in development to avoid having to login every time
      // .then(() => {
      //   getAllFlights();
      //   startPolling();
      // });
    } else {
      await getAllFlights();
      startPolling();
    }
  };

  const refreshSeatmapData = async (
    id,
    flightPrefix,
    flightNumber,
    flightDate,
    airport,
    boardPoint,
    offPoint,
    flightSTD,
    flightSTA,
    finalDestination = null
  ) => {
    // Validate required parameters
    if (!id) {
      console.warn("Cannot refresh seatmap data: flight ID is missing");
      return;
    }

    if (!flightPrefix || !flightNumber) {
      console.warn(
        "Cannot refresh seatmap data: flight prefix or number is missing"
      );
      return;
    }

    if (!navigator.onLine) {
      const passengerList = await getLocalData(dbKeyPassengerList(id));
      const seatmap = await getLocalData(dbKeySeatmap(id));
      const bagList = await getLocalData(dbKeyBagList(id));

      updateFlightData({
        passengerList: passengerList || [],
        seatmap: seatmap || [],
        bagList: bagList || { checkedInCount: 0, loadedCount: 0, bags: [] },
      });
      return;
    }

    // Skip fetching seatmap, passengerlist and baglist in training mode
    const trainingMode = isTrainingMode();
    if (trainingMode) {
      return;
    }

    await getPassengerList({
      id,
      flightPrefix,
      flightNumber,
      flightDate,
      airport,
      flightSTD,
      flightSTA,
      finalDestination,
    });

    await getSeatmap({
      id,
      flightPrefix,
      flightNumber,
      flightDate,
      boardPoint,
      offPoint,
    });

    await getBagList({
      id,
      flightPrefix,
      flightNumber,
      flightDate,
      airport: boardPoint,
    });
  };

  const saveMealModeData = async (
    flightId,
    passengersWithMeal,
    passengerIDs
  ) => {
    if (
      !flightId ||
      !Array.isArray(passengersWithMeal) ||
      !Array.isArray(passengerIDs)
    ) {
      console.error("Missing parameters in saveMealModeData");
      return;
    }

    const key = dbKeyMealMode(flightId);

    const existingPassengers = (await getLocalData(key)) || [];
    const passengerMap = new Map(existingPassengers.map((p) => [p.id, p]));

    for (const id of passengerIDs) {
      if (passengerMap.has(id)) {
        const current = passengerMap.get(id);
        passengerMap.set(id, {
          ...current,
          mealReceived: !current.mealReceived,
        });
      } else {
        const newPassenger = passengersWithMeal.find((p) => p.id === id);
        if (newPassenger) {
          passengerMap.set(id, { ...newPassenger, mealReceived: true });
        }
      }
    }

    await saveLocalData(Array.from(passengerMap.values()), key);
  };

  const getMealModeData = async (flightId) => {
    const key = dbKeyMealMode(flightId);
    const data = await getLocalData(key);
    return data;
  };

  const refreshAllData = async () => {
    await getAllFlights(); // Uses network when online, IndexedDB when offline
  };

  const getAllFlights = async () => {
    flightListSyncingRef.current = true;
    try {
    await syncPreFlightBriefing();

    // Local-first hydrate to show cached content immediately
    const localFlightsRaw = await getLocalData(dbKeyAllFlights);
    const localFlights = filterFlightsInRetentionWindow(localFlightsRaw);

    if (localFlights.length) {
      const locallySavedSelectedFlightId = await getLocalData(
        dbKeySelectedFlight
      );

      const selectedFlightIdLocal =
        (localFlights || []).find(
          (flight) =>
            flight.id === flightDataRef.current.selectedFlightId ||
            flight.id === locallySavedSelectedFlightId
        )?.id ||
        getNextUpcomingFlightId(localFlights) ||
        getFirstActiveFlightId(localFlights) ||
        getMostRecentPastFlightId(localFlights) ||
        localFlights?.[0]?.id ||
        "";

      pendingSelectedFlightIdRef.current = selectedFlightIdLocal;
      updateFlightData({
        allFlights: localFlights,
        selectedFlightId: selectedFlightIdLocal,
      });
    }

    // Refresh from network if available
    const allFlights = navigator.onLine ? await getFlights() : localFlights;

    const locallySavedSelectedFlightId = await getLocalData(
      dbKeySelectedFlight
    );

    const selectedFlightId =
      (allFlights || []).find(
        (flight) =>
          flight.id === flightDataRef.current.selectedFlightId ||
          flight.id === locallySavedSelectedFlightId
      )?.id ||
      getNextUpcomingFlightId(allFlights) ||
      getFirstActiveFlightId(allFlights) ||
      getMostRecentPastFlightId(allFlights) ||
      allFlights?.[0]?.id ||
      "";

    if (allFlights) {
      // When online, always replace with fresh network list to avoid keeping stale flights
      const flightsToUse = navigator.onLine
        ? allFlights || []
        : localFlights || [];
      pendingSelectedFlightIdRef.current = selectedFlightId;
      updateFlightData({ allFlights: flightsToUse, selectedFlightId });

      // Cleanup old per-flight cached data beyond 72h
      await cleanupOldFlightData(localFlights || [], allFlights || []);

      // Skip time-based warnings in training mode
      const trainingMode = isTrainingMode();

      if (!trainingMode) {
        const hasNext14h = hasFlightsNext14h(flightsToUse || []);
        const hasLast72h = hasFlightsLast72h(flightsToUse || []);

        if (!hasNext14h && !hasLast72h) {
          updateFlightData({
            status: "No flights found with STD within the next 11 hours",
          });
        } else {
          updateFlightData({ status: "" });
        }
      } else {
        // In training mode, clear any status messages
        updateFlightData({ status: "" });
      }
    }
    } finally {
      flightListSyncingRef.current = false;
      setFlightListSyncId((id) => id + 1);
    }
  };

  const removeFlightKeys = async (flightId) => {
    try {
      await Promise.all([
        remove(dbKeyTimestamp(flightId)),
        remove(dbKeyFlightData(flightId)),
        remove(dbKeyAirports(flightId)),
        remove(dbKeyAirportSelected(flightId)),
        remove(dbKeyReqFuel(flightId)),
        remove(dbKeyCIF(flightId)),
        remove(dbKeyCTOT(flightId)),
        remove(dbKeyCTOTCrew(flightId)),
        remove(dbKeyCTOTAck(flightId)),
        remove(dbKeyMETAR(flightId)),
        remove(dbKeySNOWTAM(flightId)),
        remove(dbKeyTAF(flightId)),
        remove(dbKeyNOTAM(flightId)),
        remove(dbKeyFlightInfoCrew(flightId)),
        remove(dbKeyWXGImages(flightId)),
        remove(dbKeyDMSFiles(flightId)),
        remove(dbKeyOFPFiles(flightId)),
        remove(dbKeyPassengerList(flightId)),
        remove(dbKeySeatmap(flightId)),
        remove(dbKeyMealMode(flightId)),
        remove(dbKeyColdSoak(flightId)),
        remove(dbKeyBagList(flightId)),
      ]);
    } catch (error) {
      console.error("Failed to remove cached keys for flight", flightId, error);
    }
  };

  // Remove old flight data that is outside the retention window (72h)
  const cleanupOldFlightData = async (previousFlights, currentFlights) => {
    const currentIds = new Set(
      (currentFlights || []).map((flight) => flight?.id).filter(Boolean)
    );
    const nowUtc = moment.utc();
    const threshold = moment.utc(nowUtc).subtract(72, "hours");

    const candidates = (previousFlights || []).filter(
      (flight) => !!flight?.id && !currentIds.has(flight.id)
    );

    await Promise.all(
      candidates.map(async (flight) => {
        const std = flight?.departureDateTime
          ? moment.utc(flight.departureDateTime)
          : null;
        if (std && std.isBefore(threshold)) {
          await removeFlightKeys(flight.id);
        }
      })
    );
  };

  // If all flights changes, refetch flight data for all flights
  useEffect(() => {
    if (flightData.allFlights.length) {
      flightData.allFlights.map((flight) => {
        loadFlightData(flight, flightData.selectedFlightId);
      });
    }
  }, [flightData.allFlights]);

  const loadFlightData = async (flight, selectedFlightId) => {
    if (flightDataRef.current.status) {
      return;
    }

    if (!flight?.id || flight.id !== selectedFlightId) {
      return;
    }

    // getAllFlights sets allFlights twice (local hydrate, then network), so the
    // effect below fires twice; skip while a load for this flight is in flight.
    if (loadingFlightIdsRef.current.has(flight.id)) return;
    loadingFlightIdsRef.current.add(flight.id);
    try {
      await loadFlightDataInner(flight);
    } finally {
      loadingFlightIdsRef.current.delete(flight.id);
    }
  };

  const loadFlightDataInner = async (flight) => {
    const { id, flightDate, sDep, sFltNo, sDest, sSTD, sSTA, sACReg } = flight;
    const isActiveFlight = () => isFlightActive(id);

    const allData = await getLocalData(dbKeyFlightData(id));

    const airports = await getLocalData(dbKeyAirports(flight?.id));
    const airportSelected = await getLocalData(
      dbKeyAirportSelected(flight?.id)
    );
    const reqFuel = await getLocalData(dbKeyReqFuel(flight?.id));
    const snowtam = await getLocalData(dbKeySNOWTAM(flight?.id));
    const metar = await getLocalData(dbKeyMETAR(flight?.id));
    const taf = await getLocalData(dbKeyTAF(flight?.id));
    const notam = await getLocalData(dbKeyNOTAM(flight?.id));
    const ctot = await getLocalData(dbKeyCTOT(flight?.id));
    const ctotCrew = await getLocalData(dbKeyCTOTCrew(flight?.id));
    const ctotAck = await getLocalData(dbKeyCTOTAck(flight?.id));
    const cif = await getLocalData(dbKeyCIF(flight?.id));
    const flightInfoCrew = await getLocalData(dbKeyFlightInfoCrew(flight?.id));
    const dataFetchTimestamp = await getLocalData(dbKeyTimestamp(flight?.id));
    const areaMessages = await getLocalData(dbKeyAreaMessages);
    const preFlightBriefing = await getLocalData(dbKeyPreFlightBriefing);
    const tkofSettings = await getLocalData(dbKeyTkofSettings(allData?.sACReg));
    const parsedWxgArray = await getLocalData(dbKeyWXGImages(flight?.id));
    const parsedDmsArray = await getLocalData(dbKeyDMSFiles(flight?.id));
    const parsedOPFArray = await getLocalData(dbKeyOFPFiles(flight?.id));

    const passengerList = await getLocalData(dbKeyPassengerList(flight?.id));
    const seatmap = await getLocalData(dbKeySeatmap(flight?.id));
    const bagList = await getLocalData(dbKeyBagList(flight?.id));
    const coldSoak = await getLocalData(dbKeyColdSoak(flight?.id));

    const ctotHasChanged = ctotCrew && ctotAck && ctotCrew.ctot !== ctotAck;

    if (!isActiveFlight()) {
      return;
    }

    updateFlightData({
      selectedFlightId: id,
      allData: allData || defaultFlightData.allData,
      airports: airports || defaultFlightData.airports,
      reqFuel: resolveStoredReqFuel(reqFuel),
      airportSelected: airportSelected || defaultFlightData.airportSelected,
      snowtam: snowtam || defaultFlightData.snowtam,
      metar: metar || defaultFlightData.metar,
      taf: taf || defaultFlightData.taf,
      notam: notam || defaultFlightData.notam,
      ctot: ctot || defaultFlightData.ctot,
      ctotCrew: ctotCrew || defaultFlightData.ctotCrew,
      ctotHasChanged: ctotHasChanged || false,
      cif: cif || defaultFlightData.cif,
      flightInfoCrew: flightInfoCrew || defaultFlightData.flightInfoCrew,
      areaMessages: areaMessages || defaultFlightData.areaMessages,
      preFlightBriefing: Number.isInteger(preFlightBriefing?.id)
        ? preFlightBriefing
        : null,
      dataFetchTimestamp:
        dataFetchTimestamp || defaultFlightData.dataFetchTimestamp,
      tkofSettings: tkofSettings || defaultFlightData.tkofSettings,
      wxgImages: parsedWxgArray || [],
      dmsFiles: parsedDmsArray || [],
      ofpFiles: parsedOPFArray || [],
      passengerList: passengerList || [],
      seatmap: seatmap || [],
      bagList: bagList || { checkedInCount: 0, loadedCount: 0, bags: [] },
      coldSoak: coldSoak || "",
    });

    if (!navigator.onLine || !isActiveFlight()) {
      return;
    }

    // Cabin crew has no use for weather, NOTAM, WXG, OFP, area messages or cold soak
    const cabin = isCabinCrew();

    const fetchSeatmapData = async () => {
      if (isTrainingMode() || !isActiveFlight()) return;

      const { flightPrefix, flightNumber } = splitFlightCode(sFltNo[0]);
      // For multi-leg flights, finalDestination is the last destination in the array
      const finalDestination =
        Array.isArray(sDest) && sDest.length > 0 ? sDest[sDest.length - 1] : null;

      // allSettled: a network error here must not stop the rest of the flight load
      await Promise.allSettled([
        getPassengerList({
          id,
          flightPrefix,
          flightNumber,
          flightDate,
          airport: sDep[0],
          flightSTD: sSTD[0],
          flightSTA: sSTA[0],
          finalDestination,
        }),
        getSeatmap({
          id,
          flightPrefix,
          flightNumber,
          flightDate,
          boardPoint: sDep[0],
          offPoint: sDest[0],
        }),
        getBagList({
          id,
          flightPrefix,
          flightNumber,
          flightDate,
          airport: sDep[0],
        }),
      ]);
    };

    // On the seatmap tab, seatmap data goes first; it only needs the flight list entry
    const seatmapFirst = window.location.pathname.endsWith("/seatmap");
    if (seatmapFirst) {
      await fetchSeatmapData();
    }

    const data = await getFlight(flight);
    if (!data || !isActiveFlight()) {
      return;
    }

    if (!cabin) {
    await getAirportsSNOWTAM(data?.airports, flight.id || "");
    await getAirportsMETAR(data?.airports, flight.id || "");
    await getAirportsTAF(data?.airports, flight.id || "");
    await getAirportsNOTAM(data?.airports, flight.id || "");
    }
    await getFlightCTOT(data?.allData, flight.id || "");
    await getFlightCTOTCrew(data?.allData, flight.id || "");
    await getFlightCIF(flight);
    await getFlightInfoCrew(flight);
    if (!cabin) {
    await cacheOFPFiles(data?.allData?.sOFP1 || [], flight.id || "");
    await cacheWxgImages(
      data?.allData?.Image_List[0]?.Image || [],
      flight.id || ""
    );

    await getAreaMessages(); // Only needed once, not per flight available.
    await getTKOFSettings(flight?.sACReg); // Only needed once, not per flight available.
    }
    await cacheDmsFiles(data?.allData?.DMS_List?.[0] || [], id || "");

    if (!isActiveFlight()) {
      return;
    }

    if (!cabin) {
    await getColdSoak({
      id,
      sACReg: sACReg[0],
      sDepIATA: sDep[0],
      sDestIATA: sDest[0],
      sFltNo: sFltNo[0],
      sDate: flightDate,
    });

      }

    if (!seatmapFirst) {
      await fetchSeatmapData();
    }

    if (!isActiveFlight()) {
      return;
    }

    const airportsN = await getLocalData(dbKeyAirports(flight?.id));
    const airportSelectedN = await getLocalData(
      dbKeyAirportSelected(flight?.id)
    );
    const reqFuelN = await getLocalData(dbKeyReqFuel(flight?.id));
    const snowtamN = await getLocalData(dbKeySNOWTAM(flight?.id));
    const metarN = await getLocalData(dbKeyMETAR(flight?.id));
    const tafN = await getLocalData(dbKeyTAF(flight?.id));
    const notamN = await getLocalData(dbKeyNOTAM(flight?.id));
    const ctotN = await getLocalData(dbKeyCTOT(flight?.id));
    const ctotCrewN = await getLocalData(dbKeyCTOTCrew(flight?.id));
    const ctotAckN = await getLocalData(dbKeyCTOTAck(flight?.id));
    const cifN = await getLocalData(dbKeyCIF(flight?.id));
    const flightInfoCrewN = await getLocalData(dbKeyFlightInfoCrew(flight?.id));
    const dataFetchTimestampN = await getLocalData(dbKeyTimestamp(flight?.id));
    const areaMessagesN = await getLocalData(dbKeyAreaMessages);
    const preFlightBriefingN = await getLocalData(dbKeyPreFlightBriefing);
    const tkofSettingsN = await getLocalData(
      dbKeyTkofSettings(allData?.sACReg)
    );
    const parsedWxgArrayN = await getLocalData(dbKeyWXGImages(flight?.id));
    const parsedDmsArrayN = await getLocalData(dbKeyDMSFiles(flight?.id));
    const parsedOPFArrayN = await getLocalData(dbKeyOFPFiles(flight?.id));
    const passengerListN = await getLocalData(dbKeyPassengerList(flight?.id));
    const seatmapN = await getLocalData(dbKeySeatmap(flight?.id));

    if (!isActiveFlight()) {
      return;
    }

    const ctotHasChangedN =
      ctotCrewN && ctotAckN && ctotCrewN.ctot !== ctotAckN;

    updateFlightData({
      allData: data?.allData || allData || defaultFlightData.allData,
      airports: airportsN || defaultFlightData.airports,
      reqFuel: resolveStoredReqFuel(reqFuelN),
      airportSelected: airportSelectedN || defaultFlightData.airportSelected,
      snowtam: snowtamN || defaultFlightData.snowtam,
      metar: metarN || defaultFlightData.metar,
      taf: tafN || defaultFlightData.taf,
      notam: notamN || defaultFlightData.notam,
      ctot: ctotN || defaultFlightData.ctot,
      ctotCrew: ctotCrewN || defaultFlightData.ctotCrew,
      ctotHasChanged: ctotHasChangedN || false,
      cif: cifN || defaultFlightData.cif,
      flightInfoCrew: flightInfoCrewN || defaultFlightData.flightInfoCrew,
      areaMessages: areaMessagesN || defaultFlightData.areaMessages,
      preFlightBriefing: Number.isInteger(preFlightBriefingN?.id)
        ? preFlightBriefingN
        : null,
      dataFetchTimestamp:
        dataFetchTimestampN || defaultFlightData.dataFetchTimestamp,
      tkofSettings:
        tkofSettingsN ||
        flightDataRef.current.tkofSettings ||
        defaultFlightData.tkofSettings,
      wxgImages: parsedWxgArrayN || flightDataRef.current.wxgImages || [],
      dmsFiles: parsedDmsArrayN || flightDataRef.current.dmsFiles || [],
      ofpFiles: parsedOPFArrayN || flightDataRef.current.ofpFiles || [],
      passengerList: passengerListN || [],
      seatmap: seatmapN || [],
    });
  };

  // Prefetch next two upcoming flights after selecting current flight
  useEffect(() => {
    const prefetchNextFlights = async () => {
      if (!navigator.onLine) return;
      const allFlights = flightDataRef.current?.allFlights || [];
      const selectedId = flightDataRef.current?.selectedFlightId || "";
      if (!allFlights.length || !selectedId) return;

      const upcoming = getNextUpcomingFlights(allFlights);
      if (!upcoming.length) return;

      // Take next two flights after the current selected flight
      const flightsToPrefetch = upcoming
        .filter(
          (f) =>
            f?.id &&
            f.id !== selectedId &&
            !prefetchedFlightIdsRef.current.has(f.id)
        )
        .slice(0, 2);

      if (!flightsToPrefetch.length) return;

      // Mark before awaiting so a second effect run does not prefetch the same flights
      flightsToPrefetch.forEach((f) => prefetchedFlightIdsRef.current.add(f.id));

      try {
        await Promise.all(
          flightsToPrefetch.map(async (flight) => {
            // Fetch base flight object and cache to IndexedDB
            const data = await getFlight(flight);

            // Prefetch dependent datasets so they are available when user switches
            if (data) {
              try {
                const cabin = isCabinCrew();
                await Promise.all([
                  ...(cabin
                    ? []
                    : [
                  getAirportsSNOWTAM(data.airports, flight.id),
                  getAirportsMETAR(data.airports, flight.id),
                  getAirportsTAF(data.airports, flight.id),
                      ]),
                  getFlightCTOT(data.allData, flight.id),
                  getFlightCTOTCrew(data.allData, flight.id),
                  getFlightCIF(flight),
                  getFlightInfoCrew(flight),
                ]);
                if (!cabin) {
                await cacheWxgImages(
                  data?.allData?.Image_List?.[0]?.Image || [],
                  flight.id || ""
                );
                await cacheOFPFiles(
                  data?.allData?.sOFP1 || [],
                  flight.id || ""
                );
                }
                await cacheDmsFiles(
                  data?.allData?.DMS_List?.[0] || [],
                  flight.id || ""
                );
              } catch (error) {
                console.warn(
                  "Prefetch data failed for flight",
                  flight?.id,
                  error
                );
              }
            }

          })
        );
      } catch (e) {
        console.error(e);
      }
    };

    prefetchNextFlights();
  }, [flightData.selectedFlightId, flightData.allFlights]);

  const updateFlightData = (update) => {
    if (!update || typeof update !== "object") return;
    setFlightData((prevState) => ({
      ...prevState,
      ...update,
    }));
  };

  useEffect(() => {
    const onPageShow = (event) => {
      if (!event.persisted) return;
      consumeEndedBriefingSession();
      if (readUnseenBriefingId() == null) {
        updateFlightData({ preFlightBriefingIsNew: false });
      }
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  const saveAndUpdateFlightData = async (data, id, key) => {
    if (!data || typeof data !== "object") return;

    if (key) {
      const dataValue = Object.values(data)?.[0];
      await saveLocalData(dataValue, key);
    }

    if (id && id === flightDataRef.current.selectedFlightId) {
      updateFlightData(data);
    }
  };

  const handleResponseError = (response, customErrorMessage) => {
    if (response.status === 401) {
      return updateFlightData({ status: SESSION_EXPIRED });
    } else if (customErrorMessage) {
      return updateFlightData({ status: customErrorMessage });
    }
  };

  const saveLocalData = async (data, key) => {
    if (data === undefined || data === null) return;

    try {
      await set(key, data);
    } catch (error) {
      console.error(`Could not save data to key ${key}`);
    }
  };

  const getLocalData = async (key) => {
    try {
      const localData = await get(key);
      return localData;
    } catch (error) {
      console.error(`Could not get data from key ${key}`);
      return null;
    }
  };

  const changeFlight = (id) => {
    const flight = findFlight(id, flightDataRef.current?.allFlights || []);
    if (!flight) {
      console.error(`Could not find flight ${id}`);
      return;
    }
    pendingSelectedFlightIdRef.current = id;
    saveLocalData(id, dbKeySelectedFlight);
    updateFlightData({ ofpFuelResetNotice: "" });
    loadFlightData(flight, flight.id);
  };

  const changeAirport = (airport) => {
    saveAndUpdateFlightData(
      { airportSelected: airport },
      flightDataRef.current.selectedFlightId,
      dbKeyAirportSelected(flightDataRef.current.selectedFlightId)
    );
  };

  const changeReqFuel = async (reqFuel) => {
    const id = flightDataRef.current.selectedFlightId;
    if (id) {
      await saveLocalData(reqFuel, dbKeyReqFuel(id));
    }
    updateFlightData({ reqFuel });
  };

  const dismissOfpFuelResetNotice = () => {
    updateFlightData({ ofpFuelResetNotice: "" });
  };

  // POLLING ---------------------------------------------------

  const startPolling = () => {
    pollFlights(true);
    pollFlight(true);
    pollCIFData(true);
    pollSNOWTAMData(true);
    pollMETAR(true);
    pollTAF(true);
    pollCTOTData(true);
    pollFlightInfoCrew(true);
    pollCacheTimestamp();
  };

  const pollCacheTimestamp = async () => {
    setTimeout(pollCacheTimestamp, 5 * 60 * 1000); // every 5 minutes

    // Do not clear cache while offline
    if (!navigator.onLine) return;

    const timestamp = await getLocalData(dbKeyLastCacheTimestamp);
    if (!timestamp) return;

    const now = getUnixTimeInSeconds();
    const hoursElapsedSinceCache = differenceInHours(now, timestamp);
    if (hoursElapsedSinceCache >= 96) {
      clear();
      saveLocalData(now, dbKeyCacheResetTimestamp);
    }
  };

  const pollFlights = async (isInitial) => {
    setTimeout(pollFlights, 30 * 60 * 1000); // every 30 minutes

    if (!navigator.onLine || isInitial || flightDataRef.current.status) return;

    getFlights();
    syncPreFlightBriefing();
  };

  const pollFlight = async (isInitial) => {
    setTimeout(pollFlight, 10 * 60 * 1000); // every 10 minutes

    if (!navigator.onLine || isInitial || flightDataRef.current.status) return;

    const now = new Date();
    const flights = flightDataRef.current.allFlights;

    flights.forEach(async (flight) => {
      const minutesFromDepature = differenceInMinutes(
        flight.departureDateTime || now,
        now
      );
      if (minutesFromDepature <= 80 && minutesFromDepature >= 0) {
        getFlight(flight);
      }
    });
  };

  const pollCIFData = async (isInitial) => {
    setTimeout(pollCIFData, 2 * 60 * 1000); // every 2 minutes

    if (!navigator.onLine || isInitial || flightDataRef.current.status) return;

    const now = new Date();
    const flights = flightDataRef.current.allFlights;

    flights.forEach(async (flight) => {
      const minutesFromDepature = differenceInMinutes(
        flight.departureDateTime || now,
        now
      );
      if (minutesFromDepature <= 30 && minutesFromDepature >= -10) {
        getFlightCIF(flight);
      }
    });
  };

  const pollCTOTData = async (isInitial) => {
    const nextPollDelay = await pollCTOTDataHelper({
      isInitial,
      flights: flightDataRef.current.allFlights,
      isOnline: navigator.onLine,
      hasErrorStatus: !!flightDataRef.current.status,
      getLocalData,
      getFlight,
      getFlightCTOT,
      getFlightCTOTCrew,
      dbKeyCTOT,
      dbKeyCTOTCrew,
    });

    setTimeout(pollCTOTData, nextPollDelay);
  };

  const pollSNOWTAMData = async (isInitial) => {
    setTimeout(pollSNOWTAMData, 30 * 60 * 1000); // every 30 minutes

    if (!navigator.onLine || isInitial || flightDataRef.current.status || isCabinCrew()) return;

    const flights = flightDataRef.current.allFlights;

    flights.forEach(async (flight) => {
      const data = await getFlight(flight);
      if (!data) return;
      getAirportsSNOWTAM(data.airports, flight.id);
    });
  };

  const pollMETAR = async (isInitial) => {
    setTimeout(pollMETAR, 30 * 60 * 1000); // every 30 minutes

    if (!navigator.onLine || isInitial || flightDataRef.current.status || isCabinCrew()) return;

    const flights = flightDataRef.current.allFlights;

    flights.forEach(async (flight) => {
      const data = await getFlight(flight);
      if (!data) return;
      getAirportsMETAR(data.airports, flight.id);
    });
  };

  const pollTAF = async (isInitial) => {
    setTimeout(pollTAF, 30 * 60 * 1000); // every 30 minutes

    if (!navigator.onLine || isInitial || flightDataRef.current.status || isCabinCrew()) return;

    const flights = flightDataRef.current.allFlights;

    flights.forEach(async (flight) => {
      const data = await getFlight(flight);
      if (!data) return;
      getAirportsTAF(data.airports, flight.id);
    });
  };

  const pollFlightInfoCrew = async (isInitial) => {
    setTimeout(pollFlightInfoCrew, 5 * 60 * 1000); // every 5 minutes

    if (!navigator.onLine || isInitial || flightDataRef.current.status) return;

    const now = new Date();
    const flights = flightDataRef.current.allFlights;

    flights.forEach(async (flight) => {
      const minutesFromDepature = differenceInMinutes(
        flight.departureDateTime || now,
        now
      );
      if (minutesFromDepature <= 60 && minutesFromDepature >= 0) {
        getFlightInfoCrew(flight);
      }
    });
  };

  // GET ---------------------------------------------------

  /**
   * Input:
    {
      "id": "DY356.20240909.OSL.BOO.LNNHA",
      "flightPrefix": "DY",
      "flightNumber": "768",
      "flightDate": "2024-09-01",
      "airport": "OSL"
    }
   */
  const getPassengerList = async (flightData) => {
    const { id } = flightData;

    if (!navigator.onLine) {
      const cached = await getLocalData(dbKeyPassengerList(id));
      if (isFlightActive(id)) {
        updateFlightData({ passengerList: cached || [] });
      }
      return cached || [];
    }

    let passengerListResponse = await fetchPassengerList(flightData);

    if (passengerListResponse.dataUnavailableInAmadeus) {
      saveLocalData(
        {
          dataUnavailableInAmadeus: true,
          message: passengerListResponse.message,
        },
        dbKeyPassengerList(id)
      );

      if (isFlightActive(id)) {
        updateFlightData({
          passengerList: {
            dataUnavailableInAmadeus: true,
            message: passengerListResponse.message,
          },
        });
      }

      return passengerListResponse;
    }

    if (passengerListResponse.success) {
      saveLocalData(passengerListResponse.data, dbKeyPassengerList(id));
      if (isFlightActive(id)) {
        updateFlightData({ passengerList: passengerListResponse.data });
      }
    }

    return passengerListResponse.success ? passengerListResponse.data : [];
  };

  /**
   * Input:
    {
      "id": "DY356.20240909.OSL.BOO.LNNHA",
      "flightPrefix": "DY",
      "flightNumber": "768",
      "flightDate": "2024-09-01",
      "boardPoint": "OSL",
      "offPoint": "TRD"
    }
   */
  const getSeatmap = async (flightData) => {
    const { id } = flightData;

    if (!navigator.onLine) {
      const cached = await getLocalData(dbKeySeatmap(id));
      if (isFlightActive(id)) {
        updateFlightData({ seatmap: cached || [] });
      }
      return cached || [];
    }

    let seatmapResponse = await fetchSeatmap(flightData);

    if (seatmapResponse.dataUnavailableInAmadeus) {
      saveLocalData(
        { dataUnavailableInAmadeus: true, message: seatmapResponse.message },
        dbKeySeatmap(id)
      );

      if (isFlightActive(id)) {
        updateFlightData({
          seatmap: {
            dataUnavailableInAmadeus: true,
            message: seatmapResponse.message,
          },
        });
      }

      return seatmapResponse;
    }

    if (seatmapResponse.success) {
      saveLocalData(seatmapResponse.data, dbKeySeatmap(id));
      if (isFlightActive(id)) {
        updateFlightData({ seatmap: seatmapResponse.data });
      }
    }

    return seatmapResponse.success ? seatmapResponse.data : [];
  };

  const getBagList = async (flightData) => {
    const { id } = flightData;

    let bagListResponse = await fetchBagList(flightData);

    if (bagListResponse.dataUnavailableInAmadeus) {
      saveLocalData(
        { dataUnavailableInAmadeus: true, message: bagListResponse.message },
        dbKeyBagList(id)
      );

      if (isFlightActive(id)) {
        updateFlightData({
          bagList: {
            dataUnavailableInAmadeus: true,
            message: bagListResponse.message,
          },
        });
      }

      return bagListResponse;
    }

    if (bagListResponse.success) {
      const bagData = bagListResponse.data;

      saveLocalData(bagData, dbKeyBagList(id));

      if (isFlightActive(id)) {
        updateFlightData({
          bagList: bagData,
        });
      }
    }

    return bagListResponse.success ? bagListResponse.data : null;
  };

  const getColdSoak = async (flightData) => {
    const { id } = flightData;

    let coldSoak = await fetchColdSoak(flightData);

    if (coldSoak) {
      saveLocalData(coldSoak, dbKeyColdSoak(id));
    }

    if (isFlightActive(id)) {
      updateFlightData({ coldSoak });
    }

    return coldSoak;
  };

  const getFlights = async () => {
    let allFlights = await getFlts(handleResponseError);
    const locallySavedSelectedFlightId = await getLocalData(
      dbKeySelectedFlight
    );

    if (allFlights) {
      const selectedFlightId =
        allFlights.find(
          (flight) =>
            flight.id === flightDataRef.current.selectedFlightId ||
            flight.id === locallySavedSelectedFlightId
        )?.id ||
        getNextUpcomingFlightId(allFlights) ||
        getFirstActiveFlightId(allFlights) ||
        getMostRecentPastFlightId(allFlights) ||
        allFlights[0]?.id ||
        "";

      const now = getUnixTimeInSeconds();
      saveLocalData(now, dbKeyLastCacheTimestamp);
      saveLocalData(allFlights, dbKeyAllFlights);
      saveLocalData(selectedFlightId, dbKeySelectedFlight);
      pendingSelectedFlightIdRef.current = selectedFlightId;
      updateFlightData({ allFlights, selectedFlightId });
    }

    return allFlights;
  };

  const getFlight = async (flight) => {
    if (!flight || !flight?.id) return;

    const flightDataObject = await getFlt(flight, handleResponseError);
    const timestamp = getUnixTimeInSeconds();

    if (flightDataObject) {
      const previousAllData = await getLocalData(dbKeyFlightData(flight?.id));
      const reqFuelKey = dbKeyReqFuel(flight?.id);
      const storedReqFuel = await getLocalData(reqFuelKey);
      const isActive = isFlightActive(flight.id);

      const { shouldResetReqFuel, notice } = resolveOfpFuelReset({
        previousFlightPlan: previousAllData,
        nextFlightPlan: flightDataObject.allData,
        inMemoryFlightPlan: isActive
          ? flightDataRef.current?.allData
          : undefined,
        storedReqFuel,
        isActiveFlight: isActive,
        computedReqFuel: flightDataObject.reqFuel,
      });

      const { reqFuel: computedReqFuel, ...flightDataWithoutReqFuel } =
        flightDataObject;

      if (isActive) {
        updateFlightData({
          ...flightDataWithoutReqFuel,
          dataFetchTimestamp: timestamp,
          ...(shouldResetReqFuel ? { reqFuel: computedReqFuel } : {}),
          ...(notice ? { ofpFuelResetNotice: notice } : {}),
        });
      }

      // Persist reqFuel before allData so a parallel getFlight cannot keep stale fuel.
      if (shouldResetReqFuel) {
        await saveLocalData(computedReqFuel, reqFuelKey);
      }
      await saveLocalData(timestamp, dbKeyTimestamp(flight?.id));
      await saveLocalData(
        flightDataObject.allData,
        dbKeyFlightData(flight?.id)
      );
      await saveLocalData(flightDataObject.airports, dbKeyAirports(flight?.id));
      await saveLocalData(
        flightDataObject.airportSelected,
        dbKeyAirportSelected(flight?.id)
      );
    }

    return flightDataObject;
  };

  const getAirportsSNOWTAM = async (airports, id) => {
    if (!airports || !airports.length || !id) return;

    const airportCodes = airports
      .map((airport) => airport.sICAO?.[0] || airport.sADICAO?.[0] || "")
      .join(" ");

    const snowtam = await getSNOWTAM(airportCodes, handleResponseError);
    snowtam &&
      (await saveAndUpdateFlightData({ snowtam }, id, dbKeySNOWTAM(id)));
  };

  const getAirportsMETAR = async (airports, id) => {
    if (!airports || !airports.length || !id) return;

    const airportCodes = airports
      .map((airport) => airport.sICAO?.[0] || airport.sADICAO?.[0] || "")
      .join(" ");

    const metar = await getMETAR(airportCodes, true);
    metar && (await saveAndUpdateFlightData({ metar }, id, dbKeyMETAR(id)));
  };

  const getAirportsTAF = async (airports, id) => {
    if (!airports || !airports.length || !id) return;

    const airportCodes = airports
      .map((airport) => airport.sICAO?.[0] || airport.sADICAO?.[0] || "")
      .join(" ");

    const taf = await getTAFV2(airportCodes, true);
    taf && (await saveAndUpdateFlightData({ taf }, id, dbKeyTAF(id)));
  };

  const getAirportsNOTAM = async (airports, id) => {
    if (!airports || !airports.length || !id) return;

    const airportCodes = airports
      .map((airport) => airport.sICAO?.[0] || airport.sADICAO?.[0] || "")
      .filter(Boolean)
      .join(" ");

    const airportCodesKey = airportCodes.split(/\s+/).sort().join(" ");

    if (fetchingNOTAMRef.current.has(airportCodesKey)) {
      return;
    }

    try {
      if (!navigator.onLine) {
        const cachedNotam = await getLocalData(dbKeyNOTAM(id));
        if (cachedNotam && isFlightActive(id)) {
          updateFlightData({ notam: cachedNotam });
        }
        return;
      }

      fetchingNOTAMRef.current.add(airportCodesKey);

      const notamResponse = await fetchNOTAM(airportCodes);
      if (notamResponse.ok) {
        const notamData = await notamResponse.json();
        const allData = flightDataRef.current?.allData;

        const filteredNotamData = Array.isArray(notamData)
          ? notamData.map((item) =>
              isFlightPlanAirport(item.icaoId, allData)
                ? stripFaaNotamFromResponse(item)
                : item,
            )
          : notamData;

        // Cache per flight
        await saveAndUpdateFlightData(
          { notam: filteredNotamData },
          id,
          dbKeyNOTAM(id),
        );

        // Cache per ICAO-kod for ad-hoc airports
        if (Array.isArray(filteredNotamData)) {
          for (const item of filteredNotamData) {
            if (item.icaoId) {
              await saveLocalData(item, dbKeyNOTAMByICAO(item.icaoId));
            }
          }
        }
      }
    } catch (error) {
      console.error("Error fetching NOTAM for airports:", error);

      if (!navigator.onLine) {
        const cachedNotam = await getLocalData(dbKeyNOTAM(id));
        if (cachedNotam && isFlightActive(id)) {
          updateFlightData({ notam: cachedNotam });
        }
      }
    } finally {
      fetchingNOTAMRef.current.delete(airportCodesKey);
    }
  };

  const getNOTAMByICAO = async (icaoCode) => {
    if (!icaoCode || icaoCode.length !== 4) return null;

    const icao = icaoCode.toUpperCase();
    const cacheKey = dbKeyNOTAMByICAO(icao);
    const allData = flightDataRef.current?.allData;

    const filterNotamResult = (notamResult) => {
      if (!notamResult) {
        return notamResult;
      }

      return isFlightPlanAirport(icao, allData)
        ? stripFaaNotamFromResponse(notamResult)
        : notamResult;
    };

    const getCachedData = async () => {
      return filterNotamResult(await getLocalData(cacheKey));
    };

    if (!navigator.onLine) {
      return await getCachedData();
    }

    try {
      const notamResponse = await fetchNOTAM(icao);
      if (!notamResponse.ok) {
        return await getCachedData();
      }

      const notamData = await notamResponse.json();

      // Find matching NOTAM data for this ICAO
      const notamResult = Array.isArray(notamData)
        ? notamData.find((item) => item.icaoId === icao) || notamData[0] || null
        : notamData || null;

      const filteredResult = filterNotamResult(notamResult);

      // Cache if we have valid data
      if (
        filteredResult &&
        (filteredResult.metar ||
          filteredResult.taf ||
          filteredResult.sNOTAM ||
          filteredResult.errorMsg)
      ) {
        await saveLocalData(filteredResult, cacheKey);
      }

      return filteredResult;
    } catch (error) {
      console.error("Error fetching NOTAM for ICAO:", icao, error);
      return await getCachedData();
    }
  };

  const getFlightCTOT = async (allData, id) => {
    if (!allData || !id) return;

    const ctot = await getCTOT(allData, handleResponseError);
    ctot && (await saveAndUpdateFlightData({ ctot }, id, dbKeyCTOT(id)));
  };

  const getFlightCTOTCrew = async (allData, id) => {
    if (!allData || !id) return;

    const ctotCrew = await getCTOTCrew(allData, handleResponseError);
    if (ctotCrew) {
      await saveLocalData(ctotCrew, dbKeyCTOTCrew(id));

      // Check if CTOT has changed
      const previousAck = await getLocalData(dbKeyCTOTAck(id));
      const ctotHasChanged = previousAck && ctotCrew.ctot !== previousAck;

      // Check if this is the first time fetching CTOT, set acknowledgment automatically
      if (!previousAck) {
        await saveLocalData(ctotCrew.ctot, dbKeyCTOTAck(id));
      }

      if (id === flightDataRef.current.selectedFlightId) {
        updateFlightData({
          ctotCrew,
          ctotHasChanged: ctotHasChanged || false,
        });
      }
    }
  };

  const acknowledgeCtotChange = async (flightId) => {
    const ctotCrew = await getLocalData(dbKeyCTOTCrew(flightId));
    if (ctotCrew && ctotCrew.ctot) {
      await saveLocalData(ctotCrew.ctot, dbKeyCTOTAck(flightId));
      if (isFlightActive(flightId)) {
        updateFlightData({ ctotHasChanged: false });
      }
    }
  };

  const getFlightCIF = async (flight) => {
    if (!flight) return;

    const cif = await getCIF(flight, handleResponseError);
    cif &&
      (await saveAndUpdateFlightData(
        { cif },
        flight?.id,
        dbKeyCIF(flight?.id)
      ));
  };

  const getFlightInfoCrew = async (flight) => {
    if (!flight) return;

    const flightInfoCrew = await getFltInfoCrew(flight, handleResponseError);
    flightInfoCrew &&
      (await saveAndUpdateFlightData(
        { flightInfoCrew },
        flight?.id,
        dbKeyFlightInfoCrew(flight?.id)
      ));
  };

  const getAreaMessages = async () => {
    const areaMessages = await getAreaMsgs(handleResponseError);
    if (areaMessages) {
      await saveLocalData(areaMessages, dbKeyAreaMessages);
      updateFlightData({ areaMessages });
    }
  };

  const publishBriefing = (topic) => {
    if (Number.isInteger(topic?.id)) writeKnownBriefingId(topic.id);
    updateFlightData(briefingState(topic));
  };

  const syncPreFlightBriefing = async () => {
    consumeEndedBriefingSession();
    const cached = await getLocalData(dbKeyPreFlightBriefing);
    const saved = Number.isInteger(cached?.id) ? cached : null;

    if (!navigator.onLine) {
      publishBriefing(saved);
      return;
    }

    const response = await getPreFlightBriefing(
      saved?.id ?? -1,
      handleResponseError
    );
    if (!response) {
      publishBriefing(saved);
      return;
    }

    const decision = resolvePreFlightBriefing(saved, response);
    if (decision.action === "clear") {
      await remove(dbKeyPreFlightBriefing);
      writeKnownBriefingId(null);
      writeUnseenBriefingId(null);
      updateFlightData({
        preFlightBriefing: null,
        preFlightBriefingIsNew: false,
      });
      return;
    }

    if (decision.action === "replace" && decision.topic) {
      const sanitized = sanitizeBriefingHtml(decision.topic.html).trim();
      if (!sanitized) {
        publishBriefing(saved);
        return;
      }

      const html = await inlineBriefingImages(sanitized);
      const topic = { id: decision.topic.id, html };
      await saveLocalData(topic, dbKeyPreFlightBriefing);
      const isNew = shouldFlagBriefingNew(
        topic.id,
        readKnownBriefingId(),
        readUnseenBriefingId(),
      );
      writeKnownBriefingId(topic.id);
      writeUnseenBriefingId(isNew ? topic.id : null);
      updateFlightData({ preFlightBriefing: topic, preFlightBriefingIsNew: isNew });
      return;
    }

    publishBriefing(decision.topic);
  };

  const acknowledgePreFlightBriefing = () => {
    if (flightListSyncingRef.current) return;
    if (!flightDataRef.current?.preFlightBriefingIsNew) return;
    writeUnseenBriefingId(null);
    updateFlightData({ preFlightBriefingIsNew: false });
  };

  /**
   * Only fetch takeoff settings for Pilot and not Crew
   */
  const getTKOFSettings = async (ACReg) => {
    const cabinCrew = isCabinCrew();

    if (!cabinCrew) {
      const tkofSettings = await fetchTKOFSettings(ACReg);
      const response = await tkofSettings.json();

      // Parse XML resposne
      parseString(response, async function (error, result) {
        const data =
          result["soap:Envelope"]["soap:Body"]?.["0"]?.[
            "AC_Perf_Settings_TKOF_RWYINFO_VarResponse"
          ]?.["0"]?.["AC_Perf_Settings_TKOF_RWYINFO_VarResult"]?.["0"];

        if (data !== null && data !== "") {
          await saveLocalData(data, dbKeyTkofSettings(ACReg));
          updateFlightData({ tkofSettings: data });
        } else {
          console.error("No take-off settings found for flight", error);
        }
      });
    }
  };

  const cacheDmsFiles = async (dmsList, flightId) => {
    const trainingMode = isTrainingMode();

    // In training mode, use ImageContent directly from DMS_List
    if (trainingMode) {
      const flattenedDmsList = flattenDmsList(dmsList, true);

      const parsedDmsArray = flattenedDmsList
        .filter((file) => file.imageContent)
        .map((file) => {
          return {
            type: file.fileType || "",
            fileUrl: file.fileUrl || "",
            fileName: file.fileName || "",
            base64: file.imageContent,
          };
        });

      if (parsedDmsArray.length > 0) {
        await saveLocalData(parsedDmsArray, dbKeyDMSFiles(flightId));
        if (isFlightActive(flightId)) {
          updateFlightData({ dmsFiles: parsedDmsArray });
        }
      }
      return;
    }

    const flattenedDmsList = flattenDmsList(dmsList, false);

    const parsedDmsArray = await Promise.all(
      flattenedDmsList.map(async (image) => {
        const base64 = await urlToBase64(image?.fileUrl);

        if (!base64) {
          console.error(`Could not convert DMS file. ${image?.fileName}`);
        }

        return {
          type: image?.fileType ?? "",
          fileUrl: image?.fileUrl ?? "",
          fileName: image?.fileName ?? "",
          base64,
        };
      })
    );

    // Only save data if we manage to fetch the DMS files.
    if (parsedDmsArray.length > 0) {
      await saveLocalData(parsedDmsArray, dbKeyDMSFiles(flightId));
      if (isFlightActive(flightId)) {
        updateFlightData({ dmsFiles: parsedDmsArray });
      }
    }
  };

  const cacheOFPFiles = async (ofpList, flightId) => {
    const validOfpList = Array.isArray(ofpList)
      ? ofpList.filter((file) => file && file.trim().length > 0)
      : [];

    const parsedOPFArray = validOfpList.map((file, index) => {
      return {
        type: "text", // Always text file
        value: file,
        fileName: `OFP file ${index + 1}`,
      };
    });

    if (parsedOPFArray.length > 0) {
      await saveLocalData(parsedOPFArray, dbKeyOFPFiles(flightId));
      if (isFlightActive(flightId)) {
        updateFlightData({ ofpFiles: parsedOPFArray });
      }
    } else {
      await saveLocalData([], dbKeyOFPFiles(flightId));
      if (isFlightActive(flightId)) {
        updateFlightData({ ofpFiles: [] });
      }
    }
  };

  const cacheWxgImages = async (imageList, flightId) => {
    const trainingMode = isTrainingMode();

    // In training mode, use ImageContent directly from Image_List
    if (trainingMode) {
      const wxgImages = imageList ?? [];
      const parsedWxgArray = wxgImages
        .filter((image) => image?.ImageContent)
        .map((image) => {
          const fileUrl = image?.sFileUrl?.[0] ?? "";
          const fileName = image?.sFileName?.[0] ?? "";
          const fileType = image?.sName?.[0]
            ? getFileType(image.sName[0])
            : fileName
            ? getFileType(fileName)
            : "gif";

          return {
            type: fileType,
            fileUrl: fileUrl,
            fileName: fileName,
            base64: image.ImageContent[0],
          };
        });

      if (parsedWxgArray.length > 0) {
        await saveLocalData(parsedWxgArray, dbKeyWXGImages(flightId));
        if (isFlightActive(flightId)) {
          updateFlightData({ wxgImages: parsedWxgArray });
        }
      }
      return;
    }

    const wxgImages = imageList ?? [];
    const parsedWxgArray = await Promise.all(
      wxgImages.map(async (image) => {
        const fileUrl = image?.sFileUrl?.[0] ?? "";
        const base64 = await urlToBase64(fileUrl);

        return {
          type: image?.sName?.[0] ?? "",
          fileUrl,
          fileName: image?.sFileName?.[0] ?? "",
          base64,
        };
      })
    );

    // Only save data if we manage to fetch the new base64 images.
    if (parsedWxgArray.length > 0) {
      await saveLocalData(parsedWxgArray, dbKeyWXGImages(flightId));
      if (isFlightActive(flightId)) {
        updateFlightData({ wxgImages: parsedWxgArray });
      }
    }
  };

  const urlToBase64 = async (url) => {
    try {
      const response = await fetch(`${url}&${Date.now()}`, { mode: "cors" });
      if (!response.ok) {
        throw new Error(`Could not fetch WXG images. ${response.status}`);
      }

      const blob = await response.blob();

      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => {
          const result = reader.result;
          if (typeof result !== "string") {
            reject(new Error("Unexpected FileReader result type"));
            return;
          }
          const commaIndex = result.indexOf(",");
          if (commaIndex === -1) {
            reject(new Error("Invalid data URL format"));
            return;
          }
          const base64data = result.slice(commaIndex + 1);
          if (!base64data) {
            reject(new Error("Empty base64 payload"));
            return;
          }
          resolve(base64data);
        };

        reader.onerror = () =>
          reject(reader.error || new Error("Failed to read blob"));
        reader.onabort = () => reject(new Error("File read aborted"));

        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error("Failed to convert url to base64", error);
      return null;
    }
  };

  // Re-initialize when training mode changes
  const reinitialize = async () => {
    const effectiveEmployeeNumber = getEffectiveEmployeeNumber();
    if (effectiveEmployeeNumber || !navigator.onLine) {
      await init();
    }
  };

  const config = {
    flightData,
    changeReqFuel,
    refreshAllData,
    changeFlight,
    changeAirport,
    getAirportsSNOWTAM,
    refreshSeatmapData,
    saveMealModeData,
    getMealModeData,
    getBagList,
    acknowledgeCtotChange,
    dismissOfpFuelResetNotice,
    reinitialize,
    getNOTAMByICAO,
    acknowledgePreFlightBriefing,
    flightListSyncId,
  };

  return (
    <DataServiceContext.Provider value={config}>
      {children}
    </DataServiceContext.Provider>
  );
};
